import React, { Suspense, useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, useGLTF, Text, ContactShadows, Environment } from '@react-three/drei';
import * as THREE from 'three';
import moment from 'moment';
import './CraneModel.css';

// 模型缩放比例
const MODEL_SCALE = 0.3;
// 实景地面模型组件
// 模型来源: Sketchfab - Brewster Road (CC署名)
function ConstructionGround() {
  const { scene } = useGLTF('/site.glb');
  return (
    <primitive 
      object={scene} 
      position={[0,-116, 0]} 
      rotation={[0, 0, 0]} 
    />
  );
}

// 仰角标注组件（在3D场景中）
function BoomAngleLabel({ angle, position }) {
  return (
    <group position={position}>
      {/* 小三角形图标 */}
      <mesh position={[-0.5, 0, 0]}>
        <coneGeometry args={[0.15, 0.3, 4]} />
        <meshStandardMaterial color="#e53e3e" />
      </mesh>
      
      {/* 角度文字 */}
      <Text 
        color="#e53e3e" 
        fontSize={0.6} 
        anchorX="center" 
        anchorY="middle"
        position={[0.3, 0, 0]}
      >
        {angle.toFixed(1)}°
      </Text>
    </group>
  );
}

// 旋转角度箭头组件
function RotationAngleLabel({ angle, position }) {
  const radius = 1;
  // 固定弧线长度（比如固定为 120°）
  const arcAngle = Math.PI * 2 / 3;  // 120°（固定值）
  
  // 创建弧线
  const curve = new THREE.EllipseCurve(
    0, 0,
    radius, radius,
    0, arcAngle,  // 只画80%的弧线，留出箭头位置
    false,
    0
  );
  
  const points = curve.getPoints(30);
  const geometry = new THREE.BufferGeometry().setFromPoints(
    points.map(p => new THREE.Vector3(p.x, 0, p.y))
  );
  
  // 箭头方向
  const arrowDir = new THREE.Vector3(
    -Math.sin(arcAngle),
    0,
    Math.cos(arcAngle)
  ).normalize();
  
  return (
    <group position={position}>
      {/* 弧线 */}
      <line geometry={geometry}>
        <lineBasicMaterial color="#f59e0b" linewidth={2} />
      </line>
      
      {/* 箭头 */}
      <primitive 
        object={new THREE.ArrowHelper(
          arrowDir,
          new THREE.Vector3(Math.cos(arcAngle) * radius, 0, Math.sin(arcAngle) * radius),
          0.3,
          0xf59e0b,
          0.15,
          0.1
        )}
      />
      
      {/* 角度文字 */}
      <Text 
        color="#f59e0b" 
        fontSize={0.5} 
        anchorX="center" 
        anchorY="middle"
        position={[0, 0.5, 0]}
      >
        {angle.toFixed(1)}°
      </Text>
    </group>
  );
}

// GLB模型组件
function GLTFCraneModel({ boomLength, bodyRotation, turretRotation, boomAngle, }) {
  const { scene } = useGLTF('/crane.glb');
  
  const nodesRef = useRef({});
  const initializedRef = useRef(false);

  React.useEffect(() => {
    if (!scene || initializedRef.current) return;
    
    scene.scale.set(MODEL_SCALE, MODEL_SCALE, MODEL_SCALE);

    // 修改颜色（针对 MeshPhysicalMaterial）
    scene.traverse((child) => {
      if (child.isMesh && child.material) {
        // 直接创建新材质，完全替换原材质
        child.material = new THREE.MeshStandardMaterial({
          color: getColorByName(child.name),  // 根据名称获取颜色
          roughness: 0.8,
          metalness: 0.2,
          transparent: false,
          opacity: 1,
          depthWrite: true,
          depthTest: true,
          blending: THREE.NormalBlending
        });
      }
    });
    
    // 辅助函数
    function getColorByName(name) {
      if (name === 'body') return '#4b5563';
      if (name === 'turret') return '#718096';
      if (name.includes('boom')) return '#d97706';
      if (name === 'pivot') return '#4b5563';
      return '#ffffff';  // 默认颜色
    }
    
    scene.traverse((child) => {
      if (child.isMesh || child.isObject3D) {
        const name = child.name.toLowerCase();
        
        if (name === 'body') nodesRef.current.body = child;
        else if (name === 'turret') nodesRef.current.turret = child;
        else if (name.includes('boom_01') || name.includes('boom1')) nodesRef.current.boom1 = child;
        else if (name.includes('boom_02') || name.includes('boom2')) nodesRef.current.boom2 = child;
        else if (name.includes('boom_03') || name.includes('boom3')) nodesRef.current.boom3 = child;
        else if (name.includes('boom_04') || name.includes('boom4')) nodesRef.current.boom4 = child;
      }
    });
    
    initializedRef.current = true;
  }, [scene]);

  useFrame(() => {
    if (!initializedRef.current) return;
    
    const nodes = nodesRef.current;
    
    // 1. 车身旋转（绕Y轴）
    if (nodes.body) {
      // 将角度转换为弧度，THREE.js 使用弧度制
      nodes.body.rotation.y = THREE.MathUtils.degToRad(bodyRotation);
    }
    
    // 2. 吊臂仰角（绕Z轴）
    if (nodes.boom1) {
      nodes.boom1.rotation.set(0, 0, 0);
      const offset = 40;  // 初始偏移量，可以调整
      nodes.boom1.rotation.z = THREE.MathUtils.degToRad(offset-boomAngle);
    }
    
    // 3. 旋转塔旋转（添加这个）
    if (nodes.turret) {
      nodes.turret.rotation.y = THREE.MathUtils.degToRad(turretRotation);  // 跟随车身旋转
      // 或者设置固定角度
      // nodes.turret.rotation.y = THREE.MathUtils.degToRad(100);
    }

    // 3. 吊臂伸长（缩放）
    if (nodes.boom1) {
      nodes.boom1.scale.set(1, 1, 1);
    }
  });

  return (
    <group position={[0, 0, 0]}>
      <primitive object={scene} />
    </group>
  );
}

// 主组件
const Crane3DViewer = ({ vehicleStatus }) => {
  // 提取数据
  const latestStatus = vehicleStatus?.latestStatus;
  const latestAlarm = vehicleStatus?.latestAlarm;
  
  // 计算模型参数
  const boomLength = latestStatus ? Math.min(100, (parseInt(latestStatus.backup_4) || 0) / 10) : 50;
  const bodyRotation = latestStatus ? (latestAlarm?.jz || 0) : 0;
  const turretRotation = latestStatus ? (parseInt(latestStatus.backup_6) / 10) : 0;
  const boomAngle = latestStatus ? (parseInt(latestStatus.backup_5) / 10) : 0;

  // 计算显示数据
  const windSpeed = latestStatus ? (parseInt(latestStatus.backup_1) / 10).toFixed(2) : '0.00';
  const momentValue = latestStatus ? (parseInt(latestStatus.backup_2) / 10).toFixed(2) : '0.00';
  const weight = latestStatus ? (parseInt(latestStatus.backup_3) / 10).toFixed(2) : '0.00';
  const boomLengthValue = latestStatus ? (parseInt(latestStatus.backup_4) / 10).toFixed(2) : '0.00';
  const boomAngleValue = latestStatus ? (parseInt(latestStatus.backup_5) / 10).toFixed(1) : '0.0';
  const rotationAngleValue = latestStatus ? (parseInt(latestStatus.backup_6) / 10).toFixed(1) : '0.0';

  // 报警数据
  const limitType = latestAlarm?.d === 'left' ? '左侵限' : '右侵限';
  const isAlarm = latestAlarm ? Math.abs(latestAlarm.py) > Math.max(latestAlarm.zyz, latestAlarm.yyz) : false;

  return (
    <div className="crane-viewer">
      {/* 顶部信息栏 */}
      <div className="crane-header">
        <div className="crane-title">
          <span>🚛</span>
          <span>{latestStatus?.vehicle_name || '车辆信息'}</span>
        </div>
        <div className="crane-actions">
          <div className="action-btn">⚙️ 设置</div>
          <div className="action-btn disabled">LOG</div>
          <div className="action-btn disabled">📊 统计</div>
          <div className="action-btn disabled">📶 信号</div>
        </div>
        <div className="update-time">
          最后更新时间: {latestStatus ? moment.utc(latestStatus.timestamp).format('YYYY-MM-DD HH:mm:ss') : '--'}
        </div>
      </div>

      {/* 主体区域 */}
      <div className="crane-body">
        {/* 左侧数据面板 */}
        <div className="left-panel">
          <div className="data-card">
            <div className="card-title">实时数据</div>
            <div className="vertical-data-list">
              <div className="vertical-data-item">
                <span className="vertical-label">臂长</span>
                <span className="vertical-value">{boomLengthValue} m</span>
              </div>
              <div className="vertical-data-item">
                <span className="vertical-label">旋转角度</span>
                <span className="vertical-value blue">{rotationAngleValue}°</span>
              </div>
              <div className="vertical-data-item">
                <span className="vertical-label">仰角</span>
                <span className="vertical-value orange">{boomAngleValue}°</span>
              </div>
              <div className="vertical-data-item">
                <span className="vertical-label">风速</span>
                <span className="vertical-value">{windSpeed} m/s</span>
              </div>
              <div className="vertical-data-item">
                <span className="vertical-label">力矩</span>
                <span className="vertical-value">{momentValue} nm</span>
              </div>
              <div className="vertical-data-item">
                <span className="vertical-label">重量</span>
                <span className="vertical-value">{weight} kg</span>
              </div>
            </div>
          </div>
        </div>

        {/* 中间3D画布 */}
        <div className="canvas-container">
          <Canvas
            camera={{ position: [10, 6, 22], fov: 50 }}
            gl={{ antialias: true }}
            dpr={1}
            resize={{ debounce: { wait: 50, maxWait: 100 } }}
          >
            <ambientLight intensity={0.5} />
            <directionalLight position={[10, 20, 10]} intensity={1.2} />
            <pointLight position={[-10, 10, -10]} intensity={0.6} />

            <Suspense fallback={null}>
              <ConstructionGround />
            </Suspense>

            <group position={[0, 0, 0]}>
              <GLTFCraneModel 
                turretRotation={turretRotation}
                boomLength={boomLength} 
                bodyRotation={bodyRotation} 
                boomAngle={boomAngle} 
              />

              <BoomAngleLabel angle={boomAngle} position={[3, 3, 0]} />
              <RotationAngleLabel angle={bodyRotation} position={[0, 0.3, 4]} />
              <ContactShadows position={[0, 0, 0]} opacity={0.6} scale={30} blur={2.5} far={10} />

              <line>
                <bufferGeometry>
                  <bufferAttribute
                    attach="attributes-position"
                    count={2}
                    array={new Float32Array([-50, 0.02, 0, 50, 0.02, 0])}
                    itemSize={3}
                  />
                </bufferGeometry>
                <lineBasicMaterial color="#ef4444" linewidth={3} />
              </line>

              <line>
                <bufferGeometry>
                  <bufferAttribute
                    attach="attributes-position"
                    count={2}
                    array={new Float32Array([0, 0.02, -50, 0, 0.02, 50])}
                    itemSize={3}
                  />
                </bufferGeometry>
                <lineBasicMaterial color="#3b82f6" linewidth={3} />
              </line>
            </group>
            <OrbitControls
              enableZoom={true}
              enablePan={false}
              minDistance={5}
              maxDistance={50}
              target={[0, 1, 0]}
            />
          </Canvas>
        </div>

        {/* 右侧报警面板 */}
        <div className="right-panel">
          <div className="data-card">
            <div className="card-title">侵限数据</div>
            <div className="vertical-data-list">
              <div className="vertical-data-item">
                <span className="vertical-label">侵限方向</span>
                <span className="vertical-value orange">{limitType || '--'}</span>
              </div>
              <div className="vertical-data-item">
                <span className="vertical-label">基准角度</span>
                <span className="vertical-value">{latestAlarm?.jz || '--'}°</span>
              </div>
              <div className="vertical-data-item">
                <span className="vertical-label">当前角度</span>
                <span className="vertical-value blue">{latestAlarm?.dq || '--'}°</span>
              </div>
              <div className="vertical-data-item">
                <span className="vertical-label">偏移量</span>
                <span className="vertical-value">{latestAlarm?.py || '--'}°</span>
              </div>
              <div className="vertical-data-item">
                <span className="vertical-label">左阈值</span>
                <span className="vertical-value">{latestAlarm?.zyz || '--'}°</span>
              </div>
              <div className="vertical-data-item">
                <span className="vertical-label">右阈值</span>
                <span className="vertical-value">{latestAlarm?.yyz || '--'}°</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Crane3DViewer;